import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormItemProps,
  Input,
  message,
  Modal,
  ModalProps,
  Radio,
  Row,
  Space,
  Typography
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { FormInstance } from 'antd/lib'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  usePersonsServiceCreateAnonymousPersonEndpoint,
  usePersonsServiceCreatePersonEndpoint,
  usePersonsServiceGetInstitutionPersonListEndpointKey,
  usePersonsServiceGetPersonListByDataEndpoint,
  usePersonsServiceGetPersonListByDataEndpointKey
} from 'src/api/queries'
import { ApiError, Countries, PersonDemographicsDto } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CountrySelect,
  CUATMTreeSelect,
  GenderSelect,
  RegionSelect
} from 'src/components'
import { useScrollToFirstError, ValidationError } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  disabledFutureDate,
  formatDate,
  legalAgePickerValue,
  yearMonthDaySlash
} from 'src/libs'
import { paths } from 'src/paths'
import { formatDates, formErrorHandler, toDayjs } from 'src/utils'

import { PersonDemographicsFields } from '../../PersonDemographics'
import { useStyles } from '../styles'

const formRules: FormItemProps['rules'] = [
  {
    min: 3
  }
]

export enum RegisterType {
  ANONYMOUS = 'anonymous',
  PERSON = 'person'
}

type Props = {
  initialValues?: AnyObject
  initialRegisterType?: RegisterType
  onCloseModal?: (form?: FormInstance) => void
} & ModalProps

export const AddNewPerson = ({
  initialValues,
  initialRegisterType = RegisterType.ANONYMOUS,
  onCloseModal,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const { onScrollToError } = useScrollToFirstError(form)

  const [registerType, setRegisterType] =
    useState<RegisterType>(initialRegisterType)
  const [shouldSearchInMConnect, setShouldSearchInMConnect] = useState(false)
  const [hasMConnectData, setHasMConnectData] = useState(false)

  const isAnonymous = registerType === RegisterType.ANONYMOUS

  const countryItem = Form.useWatch('citizenship', form)

  const isForeign = countryItem !== Countries.MD
  const idnp = Form.useWatch('idnp', form)
  const hasPrison = Form.useWatch('hasPrison', form)
  const residenceAddressRegion = Form.useWatch(
    ['residenceAddress', 'region'],
    form
  )
  const currentAddressRegion = Form.useWatch(['currentAddress', 'region'], form)

  const isCurrentAddressSameAsResidence = Form.useWatch(
    'isCurrentAddressSameAsResidence',
    form
  )

  const { data, isFetching, error, refetch, isFetchedAfterMount } =
    usePersonsServiceGetPersonListByDataEndpoint(
      {
        shouldSearchInMConnect,
        citizenship: Countries.MD,
        idnp
      },
      [idnp],
      { enabled: false }
    )

  const { mutate } = usePersonsServiceCreatePersonEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetInstitutionPersonListEndpointKey]
      })
      message.success(t('persons.add_person_success'))
      handleCancel()

      navigate(paths.persons.details(String(data?.id)))
    }
  })

  const { mutate: addAnonymousPerson } =
    usePersonsServiceCreateAnonymousPersonEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [usePersonsServiceGetInstitutionPersonListEndpointKey]
        })
        message.success(t('persons.add_person_success'))
        handleCancel()
        navigate(paths.persons.details(String(data?.id)))
      }
    })

  const handleCancel = () => {
    queryClient.removeQueries({
      queryKey: [usePersonsServiceGetPersonListByDataEndpointKey]
    })
    form.resetFields()
    onCloseModal?.(form)
  }

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      if (isAnonymous) {
        const requestBody = {
          firstName: values.firstName,
          lastName: values.lastName,
          birthDate: toDayjs(values.birthDate)?.format(yearMonthDaySlash),
          gender: values.gender
        }

        return addAnonymousPerson({
          requestBody
        })
      }

      const residenceAddress = {
        country: values.residenceAddress.citizenship,
        cuatmLocalityId: values.residenceAddress.locality,
        street: values.residenceAddress.street,
        streetNumber: values.residenceAddress.streetNumber,
        apartmentNumber: values.residenceAddress.apartmentNumber
      }

      const currentAddress = {
        country: values.currentAddress.citizenship,
        cuatmLocalityId: values.currentAddress.locality,
        street: values.currentAddress.street,
        streetNumber: values.currentAddress.streetNumber,
        apartmentNumber: values.currentAddress.apartmentNumber
      }

      mutate({
        requestBody: {
          firstName: values.firstName,
          lastName: values.lastName,
          birthDate: toDayjs(values.birthDate)?.format(yearMonthDaySlash),
          citizenship: values.citizenship,
          gender: values.gender,
          idnp: values?.idnp,
          penitentiaryRecords: hasPrison
            ? [
                {
                  prisonName: values?.prisonName,
                  fromDate: formatDate(
                    values?.penitentiaryRecordsDate[0],
                    yearMonthDaySlash
                  ),
                  toDate: formatDate(
                    values?.penitentiaryRecordsDate[1],
                    yearMonthDaySlash
                  )
                }
              ]
            : [],
          residenceAddress,
          ...(isCurrentAddressSameAsResidence
            ? { currentAddress: residenceAddress }
            : { currentAddress }),
          demographics: formatDates(
            values.demographics
          ) as PersonDemographicsDto
        }
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  const onClick = async () => {
    await form?.validateFields(['idnp'])
    const idnpForm = await form.getFieldValue('idnp')

    form.setFieldValue('idnp', idnpForm)

    refetch()
  }

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues)
    }
  }, [initialValues, form])

  useEffect(() => {
    if (!isFetchedAfterMount) return

    if (!error && !isFetching) {
      const dataLength = data?.data?.length ?? 0
      setHasMConnectData(false)

      if (!shouldSearchInMConnect && dataLength !== 0) {
        message.error(t('persons.exists_in_system'))
      } else if (!shouldSearchInMConnect && dataLength === 0) {
        setShouldSearchInMConnect(true)
      } else if (shouldSearchInMConnect && dataLength === 0) {
        message.error(t('persons.no_person_found'))
        setShouldSearchInMConnect(false)
      } else if (dataLength > 0) {
        setShouldSearchInMConnect(false)
        setHasMConnectData(true)

        const personData = data?.data?.[0]

        form?.setFieldsValue({
          firstName: personData?.firstName,
          lastName: personData?.lastName,
          gender: personData?.gender,
          birthDate: toDayjs(personData?.birthDate)
        })
      }
    }
  }, [data, error, isFetching])

  useEffect(() => {
    if (shouldSearchInMConnect === true) {
      refetch()
    }
  }, [shouldSearchInMConnect])

  useEffect(() => {
    form.setFieldValue('citizenship', Countries.MD)
  }, [])

  return (
    <Modal
      open
      width={1000}
      centered
      maskClosable={false}
      title={
        registerType === RegisterType.ANONYMOUS
          ? t('persons.add_anonymous_person')
          : t('persons.add_identified_person')
      }
      footer={false}
      onCancel={() => onCloseModal?.(form)}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      {...props}
    >
      <Form requiredMark={false} layout="vertical" form={form}>
        <Flex vertical gap={24}>
          <Radio.Group
            value={registerType}
            className={styles.radioGroup}
            onChange={(e) => setRegisterType(e.target.value)}
            options={[
              {
                value: RegisterType.ANONYMOUS,
                label: t('persons.anonymous_register')
              },
              {
                value: RegisterType.PERSON,
                label: t('persons.identified_register')
              }
            ]}
          />

          <Divider />

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="lastName"
                label={t('form.last_name')}
                rules={[...(requiredRules || []), ...(formRules || [])]}
              >
                <Input
                  placeholder={t('form.last_name')}
                  disabled={!isAnonymous && hasMConnectData}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="firstName"
                label={t('form.first_name')}
                rules={[...(requiredRules || []), ...(formRules || [])]}
              >
                <Input
                  placeholder={t('form.first_name')}
                  disabled={!isAnonymous && hasMConnectData}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="birthDate"
                label={t('form.birth_date')}
                rules={requiredRules}
              >
                <DatePicker
                  className="full-width"
                  placeholder={t('form.birth_date')}
                  defaultPickerValue={legalAgePickerValue}
                  format={dayMonthYearFormatWithDot}
                  disabled={!isAnonymous && hasMConnectData}
                />
              </Form.Item>
            </Col>

            <Col span={24} lg={12}>
              <Form.Item
                name="gender"
                label={t('form.gender')}
                rules={requiredRules}
              >
                <GenderSelect disabled={!isAnonymous && hasMConnectData} />
              </Form.Item>
            </Col>

            {isAnonymous && (
              <Col span={24}>
                <Checkbox checked={true} disabled>
                  {t('epidemiology.patient_status.anonymous')}
                </Checkbox>
              </Col>
            )}

            {!isAnonymous && (
              <>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="citizenship"
                    label={t('common.citizenship')}
                    rules={requiredRules}
                    initialValue={Countries.MD}
                  >
                    <CountrySelect
                      allowClear
                      placeholder={t('common.citizenship')}
                    />
                  </Form.Item>
                </Col>

                {!isForeign && (
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="idnp"
                      label={t('form.idnp')}
                      rules={[
                        {
                          min: 13,
                          max: 13,
                          required: true
                        }
                      ]}
                    >
                      <Input
                        placeholder={t('form.idnp')}
                        className="full-width"
                      />
                    </Form.Item>
                  </Col>
                )}

                <Col span={24} style={{ textAlign: 'right' }}>
                  <Space>
                    <Button type="primary" onClick={onClick}>
                      {t('transfer.get_data_from_mconnect')}
                    </Button>
                  </Space>
                  <Form.Item name="mconnet">
                    <Checkbox checked={hasMConnectData} disabled>
                      {t('transfer.verified_data_mconnect')}
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item name="hasPrison" valuePropName="checked">
                    <Checkbox>{t('cases.was_in_prison')}</Checkbox>
                  </Form.Item>
                </Col>

                {hasPrison && (
                  <>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="prisonName"
                        label={t('cases.prison')}
                        rules={requiredRules}
                      >
                        <Input placeholder={t('cases.prison')} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="penitentiaryRecordsDate"
                        label={t('persons.prison_record')}
                        rules={requiredRules}
                      >
                        <DatePicker.RangePicker
                          className="full-width"
                          format={dayMonthYearFormatWithDot}
                          placement="bottomRight"
                          disabledDate={disabledFutureDate}
                          placeholder={[
                            t('notifications.from_date'),
                            t('notifications.to_date')
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}

                <Col span={24}>
                  <Typography.Text>
                    {t('cases.residential_address')}
                  </Typography.Text>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['residenceAddress', 'region']}
                    label={t('organizational_structure.region')}
                    rules={requiredRules}
                  >
                    <RegionSelect
                      onChange={() => {
                        form.setFieldValue('locality', null)
                      }}
                      placeholder={t('organizational_structure.region')}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['residenceAddress', 'locality']}
                    label={t('organizational_structure.city')}
                    rules={requiredRules}
                  >
                    <CUATMTreeSelect
                      placeholder={t('organizational_structure.city')}
                      elementId={residenceAddressRegion}
                      disabled={!residenceAddressRegion}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['residenceAddress', 'street']}
                    label={t('organizational_structure.street')}
                    rules={requiredRules}
                  >
                    <Input placeholder={t('organizational_structure.street')} />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['residenceAddress', 'streetNumber']}
                    label={t('form.building')}
                  >
                    <Input placeholder="5/5" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={['residenceAddress', 'apartmentNumber']}
                    label={t('form.apartment_number')}
                  >
                    <Input placeholder="ap.90" />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={'isCurrentAddressSameAsResidence'}
                    valuePropName="checked"
                  >
                    <Checkbox>
                      {t('persons.live_address_same_current_address')}
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Typography.Text>{t('form.address')}</Typography.Text>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['currentAddress', 'region']}
                    label={t('organizational_structure.region')}
                  >
                    <RegionSelect
                      onChange={() => {
                        form.setFieldValue('locality', null)
                      }}
                      disabled={isCurrentAddressSameAsResidence}
                      placeholder={t('organizational_structure.region')}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['currentAddress', 'locality']}
                    label={t('organizational_structure.city')}
                  >
                    <CUATMTreeSelect
                      placeholder={t('organizational_structure.city')}
                      elementId={currentAddressRegion}
                      disabled={
                        !currentAddressRegion || isCurrentAddressSameAsResidence
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['currentAddress', 'street']}
                    label={t('organizational_structure.street')}
                  >
                    <Input
                      placeholder={t('organizational_structure.street')}
                      disabled={isCurrentAddressSameAsResidence}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Form.Item
                    name={['currentAddress', 'streetNumber']}
                    label={t('form.building')}
                  >
                    <Input
                      placeholder="5/5"
                      disabled={isCurrentAddressSameAsResidence}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={['currentAddress', 'apartmentNumber']}
                    label={t('form.apartment_number')}
                  >
                    <Input
                      placeholder="ap.90"
                      disabled={isCurrentAddressSameAsResidence}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          {!isAnonymous && (
            <>
              <Divider />
              <Typography.Title level={5}>
                {t('demographics.title')}
              </Typography.Title>
              <PersonDemographicsFields namePrefix={['demographics']} />
            </>
          )}

          <Flex justify="center" gap={8}>
            <Button onClick={() => onCloseModal?.(form)}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" onClick={onSubmit}>
              {t('epidemiology.save')}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
